import React, { useEffect, useState } from 'react';
import {
    Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography,
} from '@mui/material';
import EmployeeAutocomplete, { EmployeeOption } from '../common/EmployeeAutocomplete';
import SearchableSelect from '../common/SearchableSelect';
import { helpWhy } from '../../help/why';
import { ILoanCreateRequest, LoanType } from '../../interfaces/employeeLoan.interface';

interface Props {
    open: boolean;
    institutionId: string;
    saving?: boolean;
    onClose: () => void;
    onSave: (req: ILoanCreateRequest) => void;
}

const TYPES: { id: LoanType; label: string }[] = [
    { id: 'LOAN', label: 'Loan (recovered in installments)' },
    { id: 'SALARY_ADVANCE', label: 'Salary advance (recovered next payroll)' },
];

// W3-D: HR direct-create. The loan starts APPROVED with its schedule; disbursement is a separate step.
const EmployeeLoanFormDialog: React.FC<Props> = ({ open, institutionId, saving = false, onClose, onSave }) => {
    const [employee, setEmployee] = useState<EmployeeOption | null>(null);
    const [loanType, setLoanType] = useState<LoanType>('LOAN');
    const [principal, setPrincipal] = useState('');
    const [installments, setInstallments] = useState('6');
    const [startYear, setStartYear] = useState('');
    const [startMonth, setStartMonth] = useState('');
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) {
            setEmployee(null);
            setLoanType('LOAN');
            setPrincipal('');
            setInstallments('6');
            setStartYear('');
            setStartMonth('');
            setReason('');
        }
    }, [open]);

    const amount = Number(principal);
    const count = Number(installments);
    const isAdvance = loanType === 'SALARY_ADVANCE';
    const valid = !!employee && amount > 0 && (isAdvance || (count >= 1 && count <= 120));

    const yearValid = startYear === '' || (Number.isInteger(Number(startYear)) && Number(startYear) >= 2000 && Number(startYear) <= 2200);
    const createWhy = saving ? 'Saving is in progress.'
        : !employee ? 'Choose an employee first.'
        : !(amount > 0) ? 'Enter an amount above 0 first.'
        : !valid ? 'Enter a number of installments from 1 to 120.'
        : !yearValid ? 'Enter a start year from 2000 to 2200, or leave it blank.' : null;

    const submit = () => {
        if (!employee || !valid || !yearValid) return;
        onSave({
            institutionId,
            userId: employee.id,
            loanType,
            principal: amount,
            installmentCount: isAdvance ? null : count,
            startYear: startYear ? Number(startYear) : null,
            startMonth: startMonth ? Number(startMonth) : null,
            reason: reason.trim() || null,
        });
    };

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'payroll.loanForm.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>New employee loan or salary advance</DialogTitle>
            <DialogContent>
                <Stack data-help-section="payroll.loanForm.section.details" spacing={2} sx={{ mt: 1 }}>
                    <EmployeeAutocomplete data-help="payroll.loanForm.field.employee" value={employee} onChange={setEmployee} required activeOnly data-testid="loan-employee" />
                    <SearchableSelect
                        data-help="payroll.loanForm.field.type" label="Type"
                        options={TYPES}
                        value={loanType}
                        onChange={id => id && setLoanType(id as LoanType)}
                        getOptionLabel={t => t.label}
                        clearable={false}
                    />
                    <TextField
                        data-help="payroll.loanForm.field.amount" label="Amount"
                        type="number"
                        value={principal}
                        onChange={e => setPrincipal(e.target.value)}
                        inputProps={{ min: 0.01, step: '0.01' }}
                        required
                        fullWidth
                    />
                    {!isAdvance && (
                        <TextField
                            data-help="payroll.loanForm.field.installments" label="Number of monthly installments"
                            type="number"
                            value={installments}
                            onChange={e => setInstallments(e.target.value)}
                            inputProps={{ min: 1, max: 120 }}
                            helperText="The last installment absorbs any rounding remainder."
                            fullWidth
                        />
                    )}
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            First recovery month (optional, defaults to this month while its payroll is not generated, otherwise next month)
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 0.5 }}>
                            <TextField
                                data-help="payroll.loanForm.field.startMonth" label="Month (1-12)"
                                type="number"
                                size="small"
                                value={startMonth}
                                onChange={e => setStartMonth(e.target.value)}
                                inputProps={{ min: 1, max: 12 }}
                                sx={{ flex: '1 1 140px', maxWidth: 220 }}
                            />
                            <TextField
                                data-help="payroll.loanForm.field.startYear" label="Year"
                                type="number"
                                size="small"
                                value={startYear}
                                onChange={e => setStartYear(e.target.value)}
                                inputProps={{ min: 2000, max: 2200 }}
                                error={!yearValid}
                                sx={{ flex: '1 1 140px', maxWidth: 220 }}
                            />
                        </Box>
                    </Box>
                    <TextField data-help="payroll.loanForm.field.reason" label="Reason" value={reason} onChange={e => setReason(e.target.value)} multiline minRows={2} fullWidth inputProps={{ maxLength: 500 }} />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button data-help="payroll.loanForm.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button data-help="payroll.loanForm.save" {...helpWhy(createWhy ? { kind: 'status', message: createWhy } : null)} variant="contained" onClick={submit} disabled={!valid || !yearValid || saving}>Create</Button>
            </DialogActions>
        </Dialog>
    );
};

export default EmployeeLoanFormDialog;
